import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { User } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface PassengerFormProps {
  /** Index of this passenger (0-based) */
  index: number;
  /** Seat number assigned to this passenger */
  seatNumber: string;
  /** Whether this is the primary contact (first passenger) */
  isPrimary: boolean;
}

/**
 * Individual passenger form for collecting passenger details.
 *
 * Fields:
 * - Full name: Required, 2-200 characters
 * - Phone: Optional (required for primary contact if no email)
 * - Email: Optional (required for primary contact if no phone)
 * - Date of birth: Optional
 */
export function PassengerForm({ index, seatNumber, isPrimary }: PassengerFormProps) {
  const { t } = useTranslation();
  const { tenant } = useTenant();
  const {
    register,
    formState: { errors },
  } = useFormContext();

  // Get errors for this passenger
  const passengersErrors = errors.passengers as Record<number, Record<string, { message?: string }>> | undefined;
  const passengerErrors = passengersErrors?.[index];

  return (
    <div className="bg-card border rounded-lg p-4">
      {/* Header with seat info */}
      <div className="flex items-center gap-2 mb-4">
        <div className={cn(
          "w-8 h-8 rounded flex items-center justify-center text-sm font-medium",
          isPrimary ? "bg-primary text-primary-foreground" : "bg-muted"
        )}>
          {seatNumber}
        </div>
        <div className="flex-1">
          <h3 className="font-medium">
            {t("checkout.passenger", { number: index + 1 })}
            {isPrimary && (
              <span className="ml-2 text-xs text-primary font-normal">
                {t("checkout.primaryContact")}
              </span>
            )}
          </h3>
        </div>
        <User className="h-5 w-5 text-muted-foreground" />
      </div>

      <div className="space-y-4">
        {/* Full Name */}
        <div className="space-y-2">
          <Label htmlFor={`passengers.${index}.fullName`}>
            {t("checkout.fullName")} <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`passengers.${index}.fullName`}
            placeholder={t("checkout.fullName")}
            autoComplete="name"
            {...register(`passengers.${index}.fullName`, {
              required: t("checkout.validation.fullNameRequired"),
              minLength: {
                value: 2,
                message: t("checkout.validation.fullNameMinLength"),
              },
              maxLength: {
                value: 200,
                message: t("checkout.validation.fullNameMaxLength"),
              },
            })}
            className={cn(
              passengerErrors?.fullName && "border-destructive"
            )}
            aria-invalid={!!passengerErrors?.fullName}
            aria-describedby={passengerErrors?.fullName ? `passengers.${index}.fullName-error` : undefined}
          />
          {passengerErrors?.fullName && (
            <p
              id={`passengers.${index}.fullName-error`}
              className="text-sm text-destructive"
              role="alert"
            >
              {passengerErrors.fullName.message}
            </p>
          )}
        </div>

        {/* Phone (required for primary contact if no email) */}
        <div className="space-y-2">
          <Label htmlFor={`passengers.${index}.phone`}>
            {t("checkout.phone")}
            {isPrimary && <span className="text-muted-foreground text-xs ml-1">{t("checkout.orEmailRequired")}</span>}
          </Label>
          <Input
            id={`passengers.${index}.phone`}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="(555) 123-4567"
            {...register(`passengers.${index}.phone`, {
              pattern: {
                value: /^[\d\s\-\(\)\+\.]+$/,
                message: t("checkout.validation.invalidPhone"),
              },
              validate: (value) => {
                if (!value) return true; // Optional unless primary contact
                const digitsOnly = value.replace(/\D/g, "");
                return digitsOnly.length >= 10 || t("checkout.validation.phoneMinDigits");
              },
            })}
            className={cn(
              passengerErrors?.phone && "border-destructive"
            )}
            aria-invalid={!!passengerErrors?.phone}
            aria-describedby={passengerErrors?.phone ? `passengers.${index}.phone-error` : undefined}
          />
          {passengerErrors?.phone && (
            <p
              id={`passengers.${index}.phone-error`}
              className="text-sm text-destructive"
              role="alert"
            >
              {passengerErrors.phone.message}
            </p>
          )}
          {/* TCPA: transactional-SMS consent disclosure at the point of phone
              entry — the server records optedIn=true for this phone on
              booking creation, so the consent must be visible right here. */}
          <p className="text-xs text-muted-foreground">
            {t("checkout.smsConsent.disclosure")}
            {tenant?.termsAndConditionsUrl && (
              <>
                {" "}
                <a
                  href={tenant.termsAndConditionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  {t("checkout.terms.termsAndConditions")}
                </a>
              </>
            )}
          </p>
        </div>

        {/* Email (required for primary contact if no phone) */}
        <div className="space-y-2">
          <Label htmlFor={`passengers.${index}.email`}>
            {t("checkout.email")}
            {isPrimary && <span className="text-muted-foreground text-xs ml-1">{t("checkout.orPhoneRequired")}</span>}
          </Label>
          <Input
            id={`passengers.${index}.email`}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="passenger@example.com"
            {...register(`passengers.${index}.email`, {
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: t("checkout.validation.invalidEmail"),
              },
            })}
            className={cn(
              passengerErrors?.email && "border-destructive"
            )}
            aria-invalid={!!passengerErrors?.email}
            aria-describedby={passengerErrors?.email ? `passengers.${index}.email-error` : undefined}
          />
          {passengerErrors?.email && (
            <p
              id={`passengers.${index}.email-error`}
              className="text-sm text-destructive"
              role="alert"
            >
              {passengerErrors.email.message}
            </p>
          )}
        </div>

        {/* Date of Birth (optional) */}
        <div className="space-y-2">
          <Label htmlFor={`passengers.${index}.dateOfBirth`}>
            {t("checkout.dateOfBirth")} <span className="text-muted-foreground text-xs">{t("checkout.optional")}</span>
          </Label>
          <Input
            id={`passengers.${index}.dateOfBirth`}
            type="date"
            autoComplete="bday"
            {...register(`passengers.${index}.dateOfBirth`)}
            className={cn(
              passengerErrors?.dateOfBirth && "border-destructive"
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default PassengerForm;
